import React from 'react';

import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import clsx from 'clsx';

// bonnet
import { ErrorBoundary } from '@bonnet/next/error-boundary';

import { eventBus } from '@atc/cai-event-bus';

import {
    Button,
    Heading,
} from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { psxVDPPopoverCtaClick } from 'reaxl-analytics-handlers';
import { EmailCTA } from 'reaxl-email';
import {
    HotCar,
    NewlyListed,
    ReducedPrice,
} from 'reaxl-molecules';

// fetchers
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import KBBConsumerRatings from './RatingsAndReviews/KBBConsumerRatings';

export function VehicleDetailsTitleContainer({
    className,
    showRatingsAndReviews,
    onEmailCTAClick,
}) {
    const listing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);

    const {
        daysOnSite,
        isHot,
        isNewlyListed,
        isReducedPrice,
        id: listingId,
        kbbConsumerRatings,
        kbbConsumerReviewCount,
        listingTitlePSX,
        owner: { privateSeller, hideEmailForm } = {},
        title,
    } = listing;

    const listingTitle = (privateSeller && listingTitlePSX) ? listingTitlePSX : title;

    const showVehicleBadges = isHot || isNewlyListed || isReducedPrice;

    const isWebComponentReady = useSelector(vdpResultsDuck.selectors.getIsWebComponentReady);

    const handleMakeOffer = (event) => {
        eventBus.emit('psx.listingActions.openMakeOffer', {});
        sendClick(psxVDPPopoverCtaClick, event, { listingId });
    };

    const handleBadgeClick = (event, labelText) => {
        sendClick('inventoryBadgeClick', event, { labelText });
    };

    const newlyListedDescription = (
        <>
            This newly listed vehicle has been on the market for less than a month.
            {' '}
            {privateSeller && isWebComponentReady && (
                <Button
                    bsStyle="text-decorated"
                    onClick={handleMakeOffer}
                >
                    Submit an offer
                </Button>
            )}
            {!privateSeller && !hideEmailForm && (
                <EmailCTA
                    className="display-inline-block"
                    labelKey="newlyListedPopover"
                    onClick={onEmailCTAClick}
                    type="link-decorated"
                    listingId={listingId}
                    ownerId={listing?.owner?.id}
                    allowPropagation
                />
            )}
            { ((privateSeller && isWebComponentReady) || !hideEmailForm)
                && (
                    <>
                        {' '}
                        before other interested buyers.
                    </>
                )}
        </>
    );

    const reducedPriceDescription = (
        <>
            The seller dropped the price on this car!
            {' '}
            {privateSeller && isWebComponentReady && (
                <Button
                    bsStyle="text-decorated"
                    className="display-inline-block"
                    onClick={handleMakeOffer}
                >
                    Submit an Offer
                </Button>
            )}
            {!privateSeller && !hideEmailForm && (
                <EmailCTA
                    className="display-inline-block"
                    labelKey="submitAnOffer"
                    onClick={onEmailCTAClick}
                    type="link-decorated"
                    listingId={listingId}
                    ownerId={listing?.owner?.id}
                    allowPropagation
                />
            )}
        </>
    );

    const renderUrgencyDriver = (() => {
        if (isHot) {
            return (
                <HotCar
                    description="Highly likely to sell in less than 1 week."
                    enableOverlay
                />
            );
        }

        if (isNewlyListed) {
            return (
                <NewlyListed
                    {...(daysOnSite > 0 && daysOnSite < 30) && {
                        description: newlyListedDescription,
                        enableOverlay: true,
                        onClick: (event) => handleBadgeClick(event, `Listed ${daysOnSite} Day${daysOnSite > 1 ? 's' : ''} Ago`),
                    }}
                />
            );
        }

        if (isReducedPrice) {
            const labelText = 'Price Drop';

            return (
                <ReducedPrice
                    description={reducedPriceDescription}
                    enableOverlay
                    label={labelText}
                    onClick={(event) => handleBadgeClick(event, labelText)}
                />
            );
        }

        return null;
    });

    return (
        <div
            data-cmp="listingTitleContainer"
            className={clsx('row padding-horizontal-2', className)}
        >
            <div>
                <ErrorBoundary>
                    {showVehicleBadges
                        && (
                            <div
                                className="display-flex align-items-center justify-content-between margin-top-3"
                            >
                                <div>
                                    {renderUrgencyDriver()}
                                </div>
                            </div>
                        )}
                </ErrorBoundary>
                <div
                    className={clsx(
                        'display-flex align-items-center margin-bottom-sm-0  margin-bottom-3',
                        {
                            'margin-top-3': showVehicleBadges,
                            'margin-top-4': !showVehicleBadges,
                        }
                    )}
                >
                    <Heading
                        level={3}
                        className={clsx({ 'margin-bottom-sm-5': !showRatingsAndReviews })}
                        componentClass="h1"
                        id="vehicle-details-heading"
                    >
                        {listingTitle}
                    </Heading>
                </div>
                {showRatingsAndReviews
                    && (
                        <KBBConsumerRatings
                            className="margin-bottom-2 margin-bottom-sm-4"
                            kbbConsumerRatings={kbbConsumerRatings}
                            kbbConsumerReviewCount={kbbConsumerReviewCount}
                        />
                    )}
            </div>
        </div>
    );
}

export default VehicleDetailsTitleContainer;

VehicleDetailsTitleContainer.propTypes = {
    /**
   * Additional CSS classes to apply to the container.
   */
    className: PropTypes.string,
    /**
   * Boolean indicating whether to show the ratings and reviews section.
   */
    showRatingsAndReviews: PropTypes.bool,
    /**
   * Function to handle clicks on the email CTA.
   */
    onEmailCTAClick: PropTypes.func,
};
